import {
  Button,
  Chip,
  collapseColumn,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  RowStyles,
  ScrollableContent,
  simpleHeader,
  TabsWithContent,
} from "@homebound/beam";
import { useMemo, useState } from "react";

import { Loading } from "src/components";
import { noop, pluralize, queryResult } from "src/utils";
import {
  PlanBasedBidsTableV2_BidPackageFragment,
  PlanBasedBidsTableV2_BidPlanChildGroupLineFragment,
  PlanBasedBidsTableV2_BidPlanGroupFragment,
  usePlanBasedBidsTableV2_BidPlanChildGroupsQuery,
} from "./PlanBasedBidsTableV2.generated";

type PlanBasedBidsTableV2Props = { bidPackage: PlanBasedBidsTableV2_BidPackageFragment };

export function PlanBasedBidsTableV2(props: PlanBasedBidsTableV2Props) {
  const { bidPackage } = props;
  const sortedPofs = bidPackage.productOfferings.sortBy((po) => po.name);
  const [selectedTab, setSelectedTab] = useState<string>(sortedPofs[0].id);
  const query = usePlanBasedBidsTableV2_BidPlanChildGroupsQuery({
    variables: { bidPackageId: bidPackage.id, productOfferingId: selectedTab },
  });
  return (
    <ScrollableContent virtualized>
      <TabsWithContent
        alwaysShowAllTabs
        tabs={sortedPofs.map((pof) => ({
          name: pof.name,
          value: pof.id,
          render: () => {
            // When this tab is shown, we'll be using the `productOffering: selectedTab` to pull back only this product offering's bid plan groups
            if (query.loading) return <Loading />;
            return queryResult(query, ({ bidPackage }) => (
              <>
                {/* Render each bid plan group in a separate table */}
                {bidPackage.bidPlanGroups.map((bpg) => (
                  <div css={Css.mb2.$} key={bpg.id}>
                    <PlanBasedBidsTableView bidPlanGroup={bpg} />
                  </div>
                ))}
              </>
            ));
          },
        }))}
        selected={selectedTab}
        onChange={setSelectedTab}
        // TODO: implement download bidsheet
        right={<Button icon="download" label="Download Bidsheet" onClick={noop} variant="secondaryBlack" size="md" />}
      />
    </ScrollableContent>
  );
}

type PlanBasedBidsTableViewProps = { bidPlanGroup: PlanBasedBidsTableV2_BidPlanGroupFragment };

function PlanBasedBidsTableView(props: PlanBasedBidsTableViewProps) {
  const { bidPlanGroup } = props;
  const rows = useMemo(() => createRows(bidPlanGroup), [bidPlanGroup]);
  const columns = useMemo(() => createColumns(bidPlanGroup), [bidPlanGroup]);
  return (
    <div>
      <TableHeader bidPlanGroup={bidPlanGroup} />
      <GridTable columns={columns} rows={rows} id={bidPlanGroup.id} rowStyles={rowStyles} />
    </div>
  );
}

type DisplayByTypeId = Record<string, { name: string; quoted: boolean }>;
type HeaderRow = { kind: "header"; data: undefined };
type LineRow = {
  kind: "line";
  data: { line: PlanBasedBidsTableV2_BidPlanChildGroupLineFragment; display: DisplayByTypeId };
};
type Row = HeaderRow | LineRow;

// Remove default Beam's row indent for children rows
const rowStyles: RowStyles<Row> = { line: { cellCss: Css.px2.$ } };

// A column per dimension, plus a static Cost column. Each dimension column looks up the
// line's precomputed display value for its own dimension via globalOptionType.
function createColumns(bidPlanGroup: PlanBasedBidsTableV2_BidPlanGroupFragment): GridColumn<Row>[] {
  return [
    collapseColumn(),
    ...bidPlanGroup.dimensions.map((dim) =>
      column<Row>({
        header: dim.globalOptionType.name,
        line: ({ display }) => {
          const displayValue = display[dim.globalOptionType.id];
          if (!displayValue) return emptyCell;
          return displayValue.quoted ? '"' : displayValue.name;
        },
        w: "400px",
      }),
    ),
    // Trailing flex column (1fr) absorbs leftover horizontal space so each row's
    // background reaches the right edge regardless of how many fixed columns precede it.
    column<Row>({ header: emptyCell, line: () => emptyCell, w: 1 }),
    // TODO
    column<Row>({ header: "Cost", line: () => emptyCell, w: "250px" }),
  ];
}

// Simple header followed by one parent row per first-dimension option (e.g. Elevation).
// Sibling lines that share the same first-dimension option nest as collapsible children.
function createRows(bidPlanGroup: PlanBasedBidsTableV2_BidPlanGroupFragment): GridDataRow<Row>[] {
  const firstDimensionTypeId = bidPlanGroup.dimensions[0]?.globalOptionType.id;
  const groups = bidPlanGroup.childGroups
    .flatMap((bpcg) => bpcg.lines)
    .groupBy(
      (line) => line.options.find((o) => o.globalOption.type.id === firstDimensionTypeId)?.globalOption.id ?? "",
    );

  const parentRows = groups.toValues().map((lines) => {
    const [parent, ...children] = buildLineRows(lines);
    return { ...parent, children };
  });

  return [simpleHeader, ...parentRows];
}

// Each dimension cell renders the option name, or quoted when it repeats the predecessor's value.
function buildLineRows(lines: PlanBasedBidsTableV2_BidPlanChildGroupLineFragment[]): GridDataRow<LineRow>[] {
  return lines.reduce<GridDataRow<LineRow>[]>((acc, curr, currentIndex) => {
    const prevDisplay = currentIndex > 0 ? (acc[currentIndex - 1]?.data?.display ?? {}) : {};
    const optionsByTypeId = curr.options.mapToObject((rpo) => {
      const typeId = rpo.globalOption.type.id;
      const name = rpo.globalOption.name;
      return [typeId, { name, quoted: name === prevDisplay[typeId]?.name }] as const;
    });
    return [...acc, { kind: "line", id: curr.id, data: { line: curr, display: optionsByTypeId } }];
  }, []);
}

function TableHeader({ bidPlanGroup }: { bidPlanGroup: PlanBasedBidsTableV2_BidPlanGroupFragment }) {
  return (
    <div css={Css.df.aic.gap2.px2.py1.bgWhite.$}>
      <span data-testid="tableHeader" css={Css.mdSb.gray900.$}>
        {bidPlanGroup.globalOption?.name ?? "Base Plan"}
      </span>
      {bidPlanGroup.dimensions.map((dim) => (
        <Chip
          key={dim.id}
          text={`${dim.globalOptions.length} ${pluralize(dim.globalOptions.length, dim.globalOptionType.name)}`}
        />
      ))}
    </div>
  );
}
